import { ConnectKitButton } from "connectkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sepolia } from "viem/chains";
import { abi as erc20ABI, getAddress, Token } from "./ERC20";

import {
    useAccount,
    useReadContract,
    useSimulateContract,
    useSwitchChain,
    useWaitForTransactionReceipt,
    useWriteContract,
} from "wagmi";
import { EntityDetails } from "./lib/types";
import useEcho from "./lib/useEcho";
import { megaSaleABI } from "./MegaSaleABI";

type BasicPermit = {
    EntityID: Uint8Array;
    SaleUUID: Uint8Array;
    Wallet: string;
    ExpiresAt: string;
    Payload: string;
};

type AllocationPermit = {
    Permit: BasicPermit;
    ReservedAmount: string;
    MinAmount: string;
    MaxAmount: string;
};

// TODO move to config
const chainId = sepolia.id;
const saleAddress = "0xDaaC5dB633A1a4D438AcD19e043b7A6224403251" as `0x${string}`;
const token = Token.USDT;

const MegaSale = ({ selectedEntity }: { selectedEntity: EntityDetails }) => {
    const { client, generatePurchasePermit } = useEcho();
    const { address: userAddress, isConnecting, chainId: userChainId } = useAccount();
    const [permitSignature, setPermitSignature] = useState<string | null>(null);
    const [permitJSON, setPermitJSON] = useState<AllocationPermit | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // TODO move to state with input
    const tokenAmount = BigInt(3000000000);
    const price = BigInt(2);
    const lockup = false;

    const { switchChain } = useSwitchChain();
    useEffect(() => {
        if (userChainId !== chainId) {
            switchChain({ chainId: chainId });
        }
    }, [userChainId, switchChain]);

    const {
        data: erc20Balance,
        error: erc20BalanceError,
        status: erc20BalanceStatus,
    } = useReadContract({
        chainId: sepolia.id,
        address: getAddress(chainId, Token.USDT),
        abi: erc20ABI,
        functionName: "balanceOf",
        args: [userAddress!],
        query: {
            select: (data: any) => data as bigint,
            enabled: Boolean(userAddress),
        },
    });

    const purchase = useCallback(async () => {
        if (!client || !selectedEntity || !userAddress) {
            return;
        }

        try {
            const response = await generatePurchasePermit({
                entityUUID: selectedEntity.EntityUUID,
                entityType: selectedEntity.EntityType,
                walletAddress: userAddress,
            });
            if (response.Signature && response.Permit) {
                setPermitSignature(response.Signature);
                setPermitJSON(response.Permit as unknown as AllocationPermit);
                return;
            }
        } catch (error) {
            setErrorMessage("Failed to generate purchase permit: " + error);
            return;
        }

        setErrorMessage("Failed to generate purchase permit");
    }, [client, selectedEntity, userAddress, generatePurchasePermit]);

    if (errorMessage) {
        return (
            <div className="flex gap-4">
                <div className="text-red-700">{errorMessage}</div>
                <button
                    onClick={() => {
                        setErrorMessage(null);
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!userAddress) {
        return (
            <div>
                <ConnectKitButton />
                <p className="text-gray-200 text-lg font-bold">Please connect your wallet</p>
            </div>
        );
    }

    if (isConnecting) {
        return <div>Connecting...</div>;
    }

    if (!permitSignature || !permitJSON) {
        return (
            <div>
                <p>Connected wallet: {userAddress}</p>
                <p>Entity: {selectedEntity.Label}</p>
                <p className="text-gray-200 text-lg font-bold">State: Ready to Purchase</p>
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={() => {
                        purchase();
                    }}
                >
                    Start Purchase
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-gray-200">Your permit signature: {permitSignature}</p>
            <p className="text-gray-200">
                Your permit JSON:{" "}
                <span className="whitespace-pre-wrap break-words">{JSON.stringify(permitJSON, hexReplacer, 2)}</span>
            </p>

            <div className="flex flex-col gap-1">
                <p>
                    Balance [{erc20BalanceStatus}]: {erc20Balance?.toString()}
                </p>
                {erc20BalanceError && <p className="text-red-700">{erc20BalanceError.message}</p>}
            </div>

            <CommitWithApproval
                userAddress={userAddress}
                tokenAmount={tokenAmount}
                price={price}
                lockup={lockup}
                permitJSON={permitJSON}
                permitSignature={permitSignature}
            />
            <button
                className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                onClick={() => {
                    setPermitSignature(null);
                    setPermitJSON(null);
                }}
            >
                Reset
            </button>
        </div>
    );
};

const CommitWithApproval = ({
    userAddress,
    tokenAmount,
    price,
    lockup,
    permitJSON,
    permitSignature,
}: {
    userAddress: `0x${string}`;
    tokenAmount: bigint;
    price: bigint;
    lockup: boolean;
    permitJSON: AllocationPermit;
    permitSignature: string;
}) => {
    // fetch the token allowance to check if the user has approved the sale to spend the token
    const {
        data: allowance,
        error: allowanceError,
        status: allowanceStatus,
        refetch: refetchAllowance,
    } = useReadContract({
        chainId: sepolia.id,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "allowance",
        args: [userAddress, saleAddress],
        query: {
            select: (data: any) => data as bigint,
        },
    });

    // check if the user has approved the sale to spend the token
    const approvalDone = useMemo(() => (allowance ?? 0n) >= tokenAmount, [allowance, tokenAmount]);

    // simulate the approval transaction
    const {
        data: approveData,
        error: approveSimulationError,
        status: approveSimulationStatus,
    } = useSimulateContract({
        chainId: chainId,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "approve",
        args: [saleAddress, tokenAmount],
        query: {
            enabled: !approvalDone,
        },
    });

    // prepare the contract write
    const {
        writeContractAsync: approve,
        data: approveTxHash,
        status: approveStatus,
        error: approveError,
    } = useWriteContract();

    const { status: approveReceiptStatus, data: approveReceipt } = useWaitForTransactionReceipt({
        hash: approveTxHash,
    });

    // send the approval transaction
    const approveERC20 = useCallback(async () => {
        if (!approveData?.request) {
            return;
        }
        try {
            await approve(approveData?.request);
        } catch (error) {
            console.error(error);
        }
    }, [approve, approveData]);

    // on success we want to refresh the allowance so the UI reflects the current chain state
    useEffect(() => {
        if (approveReceiptStatus === "success") {
            refetchAllowance();
        }
    }, [approveReceiptStatus, refetchAllowance]);

    // simulate the commit with approval
    const {
        data: commitWithApprovalData,
        error: commitWithApprovalSimulationError,
        status: commitWithApprovalSimulationStatus,
    } = useSimulateContract({
        address: saleAddress,
        abi: megaSaleABI,
        functionName: "replaceBidWithApproval",
        args: [
            {
                price: price,
                amount: tokenAmount,
                lockup: lockup,
            },
            {
                permit: {
                    entityID: arrayToHex(permitJSON.Permit.EntityID),
                    saleUUID: arrayToHex(permitJSON.Permit.SaleUUID),
                    wallet: permitJSON.Permit.Wallet as `0x${string}`,
                    expiresAt: BigInt(permitJSON.Permit.ExpiresAt),
                    payload: base64ToHex(permitJSON.Permit.Payload),
                },
                reservedAmount: BigInt(permitJSON.ReservedAmount),
                minAmount: BigInt(permitJSON.MinAmount),
                maxAmount: BigInt(permitJSON.MaxAmount),
            },
            base64ToHex(permitSignature),
        ] as const,
        query: {
            enabled: Boolean(permitJSON) && Boolean(permitSignature) && approvalDone,
        },
    });

    console.log(commitWithApprovalData, Boolean(permitJSON), Boolean(permitSignature), approvalDone);

    // prepare the contract write
    const {
        writeContractAsync: writeCommit,
        data: commitTxHash,
        status: commitStatus,
        error: commitError,
    } = useWriteContract();

    const { status: commitReceiptStatus, data: commitReceipt } = useWaitForTransactionReceipt({
        hash: commitTxHash,
    });

    // send the commit transaction
    const commit = useCallback(async () => {
        if (!commitWithApprovalData?.request) {
            return;
        }

        try {
            await writeCommit(commitWithApprovalData?.request);
        } catch (error) {
            console.error(error);
        }
    }, [writeCommit, commitWithApprovalData]);

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                    <p>
                        Allowance [{allowanceStatus}]: {allowance?.toString()}
                    </p>
                    {allowanceError && <p className="text-red-700">{allowanceError.message}</p>}
                </div>
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={approveERC20}
                    disabled={!approveData?.request}
                >
                    Approve
                </button>
                <div className="flex flex-col gap-1">
                    <p>Approval simulation status: {approveSimulationStatus}</p>
                    {approveSimulationError && (
                        <p className="text-red-700">
                            Error simulating approval: {approveSimulationError?.message} {approveSimulationError?.name}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Approve write: {approveStatus}</p>
                    {approveStatus === "success" && <p className="text-green-700">Approval sent: {approveTxHash}</p>}
                    {approveError && <p className="text-red-700">{approveError.message}</p>}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Approve receipt status: {approveReceiptStatus}</p>
                    {approveReceipt && (
                        <p className="text-green-700">
                            {approveReceipt.status} {approveReceipt.transactionHash}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={commit}
                    disabled={!commitWithApprovalData?.request}
                >
                    Send commitment
                </button>
                <div className="flex flex-col gap-1">
                    <p>Commit simulation status: {commitWithApprovalSimulationStatus}</p>
                    {commitWithApprovalSimulationError && (
                        <p className="text-red-700">
                            Error simulating commitment: {commitWithApprovalSimulationError?.message}{" "}
                            {commitWithApprovalSimulationError?.name}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit write status: {commitStatus}</p>
                    {commitStatus === "success" && <p className="text-green-700">Commitment sent: {commitTxHash}</p>}
                    {commitError && <p className="text-red-700">{commitError.message}</p>}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit receipt status: {commitReceiptStatus}</p>
                    {commitReceipt && (
                        <p className="text-green-700">
                            {commitReceipt.status} {commitReceipt.transactionHash}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

function isNumberArray(value: unknown): value is number[] {
    return (
        Array.isArray(value) && value.every((v) => typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 255)
    );
}

function hexReplacer(_key: string, value: unknown): unknown {
    if (value instanceof Uint8Array || isNumberArray(value)) {
        return arrayToHex(Array.from(value));
    }
    return value;
}

function arrayToHex(arr: Uint8Array | number[] | undefined | null): `0x${string}` {
    if (!arr) {
        return "0x";
    }

    return `0x${Array.from(arr)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")}`;
}

function base64ToHex(base64: string | null): `0x${string}` {
    if (!base64) {
        return "0x";
    }
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return arrayToHex(bytes);
}

export default MegaSale;
